import {
	STEPPER_GLOBAL_OPTIONS,
	StepperSelectionEvent,
} from '@angular/cdk/stepper';
import {
	AfterViewInit,
	Component,
	Inject,
	OnInit,
	ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { ModalData } from '@datachain/ui-sdk/components/app-modal';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Store } from '@ngrx/store';
import { Map, OrderedSet } from 'immutable';
import {
	BehaviorSubject,
	combineLatest,
	distinctUntilChanged,
	firstValueFrom,
	Observable,
	takeUntil,
	tap,
} from 'rxjs';

import { DcIcons } from '../../../ui/app-dc.icons';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import { AccessConfigEntity } from '../../domain/exposition-access-config.entity';
import {
	ColumnConfigErrorKey,
	ColumnConfigListError,
	ExpositionColumnConfigListEntity,
} from '../../domain/exposition-column-config-list.entity';
import { ExpositionColumnConfigEntity } from '../../domain/exposition-column-config.entity';
import {
	ExpositionDiagnosticError,
	ExpositionErrorKey,
} from '../../domain/exposition-diagnostic-errors.entity';
import { ExpositionProvider } from '../../providers/exposition.provider';
import {
	closeCreateExpositionModal,
	createExposition,
	ExpositionsSelector,
	getAvailableConsumers,
	getColumnConfigList,
	initExpositionStepper,
	refreshDatablockColumns,
	refreshDatablocks,
} from '../../store';
import { ExpositionAccessConfigComponent } from '../exposition-access-config/exposition-access-config.component';
import { ExpositionColumnsConfigExtendedComponent } from '../exposition-columns-config-extended/exposition-columns-config-extended.component';
import { ExpositionColumnsConfigComponent } from '../exposition-columns-config/exposition-columns-config.component';
// eslint-disable-next-line max-len
import { ExpositionEndpointMetadataConfigComponent } from '../exposition-endpoint-metadata-config/exposition-endpoint-metadata-config.component';
// eslint-disable-next-line max-len
import { ExpositionEndpointMetadataEntity } from '../exposition-endpoint-metadata-config/exposition-endpoint-metadata.entity';
// eslint-disable-next-line max-len
import { ExpositionInternalMetadataConfigComponent } from '../exposition-internal-metadata-config/exposition-internal-metadata.config.component';
import { ExpositionMetadataEntity } from '../exposition-internal-metadata-config/exposition-metadata.entity';
import {
	ExpositionStep,
	ExpositionStore,
	IExpositionStepperValidity,
} from './exposition-store.service';

@Component({
	selector: 'app-expositions-stepper',
	templateUrl: './exposition-stepper.component.html',
	styleUrls: ['./exposition-stepper.component.scss'],
	providers: [
		ExpositionStore,
		ExpositionProvider,
		{
			provide: STEPPER_GLOBAL_OPTIONS,
			useValue: {
				displayDefaultIndicatorType: false,
			},
		},
	],
})
export class ExpositionStepperComponent<
		T extends { datablockId: number; datablockLabel: string }
	>
	extends DcBaseComponent
	implements OnInit, AfterViewInit
{
	protected readonly DcIcons = DcIcons;
	protected readonly ExpositionStep = ExpositionStep;
	protected readonly ColumnConfigErrorKey = ColumnConfigErrorKey;

	public isDatablockSelectionEnabledSubject = new BehaviorSubject<boolean>(
		true
	);

	public currentErrorSubject = new BehaviorSubject<
		ExpositionDiagnosticError | undefined
	>(undefined);

	public vo$!: Observable<{
		isCreationInProgress: boolean;
		isDatablockSelectionEnabled: boolean;
		currentStep: number;
		expositionMetadata: ExpositionMetadataEntity;
		endpointMetadata: ExpositionEndpointMetadataEntity;
		columnsConfiguration: ExpositionColumnConfigListEntity;
		columnsSelection: ExpositionColumnConfigListEntity;
		accessConfig: AccessConfigEntity;
		isCurrentStepValid: boolean;
		availableTags: OrderedSet<AppTagItemEntity>;
		listErrors: Map<ColumnConfigErrorKey, ColumnConfigListError>;
		stepsValidity: IExpositionStepperValidity;
		currentStepError: ExpositionDiagnosticError | undefined;
	}>;

	@ViewChild(MatStepper) public stepper: MatStepper | null = null;
	@ViewChild('columnsSelectionGrid')
	public columnsSelectionCmp: ExpositionColumnsConfigComponent | null = null;
	@ViewChild(ExpositionColumnsConfigExtendedComponent)
	public columnsConfigCmp: ExpositionColumnsConfigExtendedComponent | null = null;

	@ViewChild(ExpositionInternalMetadataConfigComponent)
	public expositionInternalMetadataCmp: ExpositionInternalMetadataConfigComponent | null =
		null;
	@ViewChild(ExpositionEndpointMetadataConfigComponent)
	public expositionEndpointMetadataCmp: ExpositionEndpointMetadataConfigComponent | null =
		null;
	@ViewChild(ExpositionAccessConfigComponent)
	public accessConfigComponentCmp: ExpositionAccessConfigComponent | null = null;

	private readonly colConfigListErrorsSubject = new BehaviorSubject<
		Map<ColumnConfigErrorKey, ColumnConfigListError>
	>(Map());

	private readonly errorsByStep: Record<
		ExpositionStep,
		Array<ExpositionErrorKey>
	> = {
		[ExpositionStep.Datablock]: [ExpositionErrorKey.NoDatablockSelection],
		[ExpositionStep.Metadata]: [
			ExpositionErrorKey.ExpositionMetadataNotValid,
			ExpositionErrorKey.ExpositionMetadataMissingFields,
			ExpositionErrorKey.EndpointMetadataMissingFields,
			ExpositionErrorKey.EndpointMetadataNotValid,
		],
		[ExpositionStep.ColumnsSelection]: [ExpositionErrorKey.NoActiveColumns],
		[ExpositionStep.ColumnsConfig]: [
			ExpositionErrorKey.DuplicatedAliasColumns,
			ExpositionErrorKey.InvalidColumns,
		],
		[ExpositionStep.Access]: [
			ExpositionErrorKey.AccessMetadataMissingFields,
			ExpositionErrorKey.NoActiveAccessColumns,
			ExpositionErrorKey.NoSelectAccessConsumers,
		],
	};

	public constructor(
		private readonly store: Store,
		private readonly stepperStore: ExpositionStore,
		private readonly expositionsSelector: ExpositionsSelector,
		private readonly expositionProvider: ExpositionProvider,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T
	) {
		super();
		this.cmpId = 'exposition-stepper';
	}

	public ngOnInit(): void {
		super.ngOnInit();

		if (this.modalData.datablockId || this.modalData.datablockLabel) {
			this.isDatablockSelectionEnabledSubject.next(false);
			this.stepperStore.startOnSecondStep(
				this.modalData.datablockId,
				this.modalData.datablockLabel
			);
		}

		this.store.dispatch(
			initExpositionStepper({
				datablockId: this.modalData.datablockId
					? +this.modalData.datablockId
					: null,
			})
		);

		this.expositionsSelector
			.getColumnConfigList$()
			.pipe(
				takeUntil(this.onDestroy$),
				distinctUntilChanged(),
				tap((columnsConfig) => {
					this.stepperStore.setColumnsSelection(columnsConfig);
				})
			)
			.subscribe();

		combineLatest([
			this.stepperStore.currentStep$,
			this.expositionProvider.errors$,
		])
			.pipe(
				takeUntil(this.onDestroy$),
				tap(([step, errors]) => {
					this.currentErrorSubject.next(
						this.expositionProvider.getFirstError(this.errorsByStep[step])
					);
					this.stepperStore.setValidity(
						!this.errorsByStep[step].some((key) => errors.has(key))
					);
				})
			)
			.subscribe();

		this.vo$ = combineAll({
			isCreationInProgress: this.expositionsSelector.isSavingInProgress$(),
			isDatablockSelectionEnabled: this.isDatablockSelectionEnabledSubject,
			currentStep: this.stepperStore.currentStep$,
			expositionMetadata: this.stepperStore.metadata$,
			endpointMetadata: this.stepperStore.endpointMetadata$,
			accessConfig: this.stepperStore.accessConfig$,
			availableTags: this.expositionsSelector.getAvailableTags$(),
			isCurrentStepValid: this.stepperStore.isCurrentStepValid$,
			stepsValidity: this.stepperStore.stepsValidity$,
			listErrors: this.colConfigListErrorsSubject,
			currentStepError: this.currentErrorSubject,
			columnsConfiguration: this.stepperStore.columnsConfiguration$,
			columnsSelection: this.stepperStore.columnsSelection$,
		});
	}

	public ngAfterViewInit(): void {
		super.ngAfterViewInit();
		if (this.stepper === null) {
			console.error('stepper not found');
			return;
		}
	}

	public nextStep(): void {
		if (this.stepper === null) {
			return;
		}
		this.stepper.next();
	}

	public previousStep(): void {
		if (this.stepper === null) {
			return;
		}
		this.stepper.previous();
	}

	public close(): void {
		this.store.dispatch(
			closeCreateExpositionModal({
				datablockId: +this.modalData.datablockId,
			})
		);
	}

	public changeCurrentStep($event: StepperSelectionEvent): void {
		if (
			!this.isDatablockSelectionEnabledSubject.value &&
			$event.selectedIndex === ExpositionStep.Datablock
		) {
			return;
		}
		this.stepperStore.changeStep({
			currentStep: $event.selectedIndex,
			previousStep: $event.previouslySelectedIndex,
		});
	}

	public async onStepReady(): Promise<void> {
		const currentStep = await firstValueFrom(this.stepperStore.currentStep$);
		const previousStep = await firstValueFrom(this.stepperStore.previousStep$);
		if (!this.stepper) {
			return;
		}

		const currentState = await firstValueFrom(this.stepperStore.state$);

		if (
			previousStep === ExpositionStep.Metadata &&
			this.expositionInternalMetadataCmp &&
			this.expositionEndpointMetadataCmp
		) {
			const internalMetadata =
				await this.expositionInternalMetadataCmp.extractFormData();
			const endPointMetadata =
				await this.expositionEndpointMetadataCmp.extractFormData();
			this.stepperStore.setMetadata(internalMetadata, endPointMetadata);
		}

		if (
			this.columnsSelectionCmp &&
			this.columnsConfigCmp &&
			currentStep === ExpositionStep.Access &&
			previousStep === ExpositionStep.ColumnsConfig
		) {
			this.stepperStore.setColumnsSelection(
				this.columnsSelectionCmp.getUpdatedList()
			);

			const existingColumnsConfig = this.columnsConfigCmp.getUpdatedList();

			const accessConfig = await firstValueFrom(
				this.stepperStore.accessConfig$
			);

			const hashedColumns = existingColumnsConfig.elements
				.filter((c) => c.isHashed)
				.toArray()
				.map(([id]) => id);

			const hiddenColumns = existingColumnsConfig.elements
				.filter((c) => c.isHidden)
				.toArray()
				.map(([id]) => id);

			const cloned = existingColumnsConfig
				.cloneActiveColumns(accessConfig.columnsConfig.elements)
				.setHashedAndHiddenColumns(hashedColumns, hiddenColumns);

			this.stepperStore.setAccessConfig(
				accessConfig.updateColumnsConfig(cloned)
			);

			this.store.dispatch(getAvailableConsumers());
		}

		if (
			this.columnsConfigCmp &&
			previousStep === ExpositionStep.ColumnsConfig
		) {
			const currentColumns = await firstValueFrom(
				this.stepperStore.columnsSelection$
			);
			const updatedList = this.columnsConfigCmp.getUpdatedList();

			const updatedElements = currentColumns.elements.reduce<
				Map<string, ExpositionColumnConfigEntity>
			>((acc, curr) => {
				const item = updatedList.elements.get(curr.id);

				if (!item) {
					return acc.set(
						curr.id,
						curr.position !== 0 ? curr.updatePosition(0) : curr
					);
				}
				return acc.set(curr.id, item);
			}, Map());
			this.stepperStore.setColumnsSelection(
				updatedList.setElements(updatedElements)
			);
		}

		if (
			(previousStep === ExpositionStep.ColumnsSelection ||
				previousStep === ExpositionStep.ColumnsConfig) &&
			(currentStep === ExpositionStep.Datablock ||
				currentStep === ExpositionStep.Metadata)
		) {
			const previouslySelectedId = await firstValueFrom(
				this.stepperStore.selectedDatablockId$
			);
			this.stepperStore.setDatablockId(previouslySelectedId, false, {
				...currentState.stepsValidity,
			});
		}

		if (
			currentStep === ExpositionStep.ColumnsSelection &&
			(previousStep === ExpositionStep.Metadata ||
				previousStep === ExpositionStep.Datablock)
		) {
			const hasSelectionChanged = await firstValueFrom(
				this.stepperStore.hasDatablockSelectionChanged$
			);
			if (!hasSelectionChanged) {
				return;
			}

			const datablockId = await firstValueFrom(
				this.stepperStore.selectedDatablockId$
			);
			this.store.dispatch(
				getColumnConfigList({
					datablockId,
				})
			);
		}

		if (
			previousStep === ExpositionStep.Access &&
			this.accessConfigComponentCmp
		) {
			this.stepperStore.setAccessConfig(
				this.accessConfigComponentCmp.getAccessConfig()
			);
		}
	}

	public async changeDatablockSelection(id: number): Promise<void> {
		const currentState = await firstValueFrom(this.stepperStore.state$);
		const selectionChanged = id !== currentState.selectedDatablockId;
		this.stepperStore.setDatablockId(id, selectionChanged, {
			...currentState.stepsValidity,
			step1: true,
		});

		const datablockLabel = await firstValueFrom(
			this.expositionsSelector.getDatablockLabel$(id)
		);
		if (datablockLabel) {
			const metadata = await firstValueFrom(this.stepperStore.metadata$);
			this.stepperStore.setExpositionInternalMetadata(
				metadata.updateLabel(datablockLabel)
			);
		}
	}

	public async modifyColumnSelectionValidity(
		errors: Map<ColumnConfigErrorKey, ColumnConfigListError>
	): Promise<void> {
		const currentState = await firstValueFrom(this.stepperStore.state$);
		if (
			currentState.currentStep === ExpositionStep.ColumnsSelection &&
			this.columnsSelectionCmp
		) {
			const updatedList = this.columnsSelectionCmp.getUpdatedList();

			this.stepperStore.setColumnsSelection(updatedList);
			this.colConfigListErrorsSubject.next(errors);
		}
	}

	public refreshDatablocks(): void {
		this.store.dispatch(refreshDatablocks());
	}

	public async refreshColumns(): Promise<void> {
		const datablockId = await firstValueFrom(
			this.stepperStore.selectedDatablockId$
		);

		this.store.dispatch(
			refreshDatablockColumns({
				datablockId,
			})
		);
	}

	// FIXME: handle error isSaveInProgress on success and error
	public async create(): Promise<void> {
		if (!this.accessConfigComponentCmp) {
			return;
		}
		const datablockId = await firstValueFrom(
			this.stepperStore.selectedDatablockId$
		);
		const metadata = await firstValueFrom(this.stepperStore.metadata$);
		const endpointMetadata = await firstValueFrom(
			this.stepperStore.endpointMetadata$
		);
		const columns = await firstValueFrom(this.stepperStore.columnsSelection$);

		this.store.dispatch(
			createExposition({
				payload: {
					datablockId,
					metadata,
					endpointMetadata,
					accessConfig: this.accessConfigComponentCmp.getAccessConfig(),
					columnsConfig: columns.elements.toArray().map(([, column]) => column),
				},
			})
		);
	}
}
